import type {
  BackendQcCheckFindingDto,
  QcEvidencePolygonDto,
} from '../../types/playground-api-types'
import type {
  Layer,
  NormalizedPoint,
  PolygonShape,
  PolylineShape,
  RectangleShape,
  Shape,
} from '@/types/pdf-viewer'
import { usePdfDocStore } from '@/stores/usePdfDocStore'
import { AI_STATUS_ACCENT, normalizeAiStatus } from './qc-status'

const MAX_LOCATABLE_EXTENT = 0.9

const DEFAULT_RECT_STYLE: RectangleShape['style'] = {
  fill: '#3B82F6',
  stroke: '#1D4ED8',
  strokeWidth: 1,
  opacity: 0.3,
}

const DEFAULT_POLYGON_STYLE: PolygonShape['style'] = {
  fill: 'rgba(0,0,0,0)',
  stroke: '#1D4ED8',
  strokeWidth: 1.5,
  opacity: 0.85,
}

function clamp01(n: number): number {
  return Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : 0
}

export function trimLeadingNewlines(text: string): string {
  if (typeof text !== 'string') return ''
  return text.replace(/^\n+/, '')
}

export interface NormalizedBbox {
  x: number
  y: number
  w: number
  h: number
}

export function normalizeQcBbox(
  bbox: {
    page?: number
    x?: number
    y?: number
    w?: number
    h?: number
  },
  pageSize?: { width: number; height: number } | null,
): NormalizedBbox | null {
  const { x, y, w, h } = bbox
  if (x == null || y == null || w == null || h == null) return null
  if (w <= 0 || h <= 0) return null
  if (x === 0 && y === 0) return null

  const looksNormalized = x <= 1 && y <= 1 && x + w <= 1.0001 && y + h <= 1.0001
  let norm: NormalizedBbox | null = null
  if (looksNormalized) {
    norm = { x: clamp01(x), y: clamp01(y), w: clamp01(w), h: clamp01(h) }
  } else if (pageSize && pageSize.width > 0 && pageSize.height > 0) {
    norm = {
      x: clamp01(x / pageSize.width),
      y: clamp01(y / pageSize.height),
      w: clamp01(w / pageSize.width),
      h: clamp01(h / pageSize.height),
    }
  }

  if (!norm) return null

  if (norm.w >= MAX_LOCATABLE_EXTENT || norm.h >= MAX_LOCATABLE_EXTENT) {
    return null
  }

  return norm
}

function boxToRectangleShape(
  documentId: string,
  layerId: string,
  pageIndex: number,
  id: string,
  x: number,
  y: number,
  w: number,
  h: number,
  name?: string,
  style?: Partial<RectangleShape['style']>,
): RectangleShape | null {
  const xN = clamp01(x)
  const yN = clamp01(y)
  const wN = clamp01(w)
  const hN = clamp01(h)
  if (xN === 0 && yN === 0 && wN === 0 && hN === 0) return null
  return {
    id,
    documentId,
    pageIndex,
    type: 'rectangle',
    layerId,
    isUserMarkup: false,
    locked: true,
    createdAt: new Date().toISOString(),
    coords: { x: xN, y: yN, width: wN, height: hN },
    style: { ...DEFAULT_RECT_STYLE, ...style },
    rotation: 0,
    visible: true,
    name,
  }
}

function polygonPointsFromDto(
  points: QcEvidencePolygonDto['points'],
): NormalizedPoint[] {
  return (points ?? [])
    .filter(
      (p): p is Required<typeof p> =>
        p.x != null &&
        p.y != null &&
        Number.isFinite(p.x) &&
        Number.isFinite(p.y),
    )
    .map((p) => ({ x: clamp01(p.x), y: clamp01(p.y) }))
}

function polygonToShape(
  documentId: string,
  layerId: string,
  id: string,
  poly: QcEvidencePolygonDto,
  points: NormalizedPoint[],
  name: string | undefined,
  style: Partial<PolygonShape['style']>,
): PolygonShape | PolylineShape {
  const pageIndex = poly.page != null && poly.page >= 1 ? poly.page - 1 : 0
  return {
    id,
    documentId,
    pageIndex,
    type: poly.closed === false ? 'polyline' : 'polygon',
    layerId,
    isUserMarkup: false,
    locked: true,
    createdAt: new Date().toISOString(),
    points,
    style: { ...DEFAULT_POLYGON_STYLE, ...style },
    visible: true,
    name,
  }
}

function qcLayerStyle(status?: string): Partial<RectangleShape['style']> {
  const accent = AI_STATUS_ACCENT[normalizeAiStatus(status)]
  return {
    fill: accent.accentColor,
    stroke: accent.accentPillText,
    opacity: 0.25,
  }
}

function slugifyLayerName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export function normalizeLayersFromQcFindings(
  documentId: string,
  findings: BackendQcCheckFindingDto[],
): Layer[] {
  const shapesByLayer = new Map<string, Shape[]>()
  const seenRegionsByLayer = new Map<string, Set<string>>()

  findings.forEach((finding) => {
    if (finding.isImplicitAiComment) return
    const layerNames = finding.evidenceLayers ?? []
    const polygons = finding.evidencePolygons ?? []
    const bboxes = finding.evidenceBboxes ?? []
    if (
      layerNames.length === 0 ||
      (polygons.length === 0 && bboxes.length === 0)
    ) {
      return
    }
    const findingStyle = qcLayerStyle(finding.status)

    layerNames.forEach((layerName) => {
      const layerId = `${documentId}-qc-layer-${slugifyLayerName(layerName)}`
      const existing = shapesByLayer.get(layerName) ?? []
      const seenRegions = seenRegionsByLayer.get(layerName) ?? new Set<string>()

      if (polygons.length > 0) {
        polygons.forEach((poly, index) => {
          const points = polygonPointsFromDto(poly.points)
          if (points.length < 3) return
          const pageIndex =
            poly.page != null && poly.page >= 1 ? poly.page - 1 : 0
          const regionKey = `poly:${pageIndex}:${points.map((p) => `${p.x.toFixed(3)},${p.y.toFixed(3)}`).join('|')}`
          if (seenRegions.has(regionKey)) return
          seenRegions.add(regionKey)
          existing.push(
            polygonToShape(
              documentId,
              layerId,
              `${layerId}-${finding.id ?? 'f'}-poly-${index}`,
              poly,
              points,
              finding.name ?? layerName,
              findingStyle,
            ),
          )
        })
      } else {
        bboxes.forEach((b, index) => {
          const pageIndex = b.page != null && b.page >= 1 ? b.page - 1 : 0
          const pageSize = usePdfDocStore
            .getState()
            .getPageSize(documentId, pageIndex)
          const norm = normalizeQcBbox(b, pageSize)
          if (!norm) return
          const regionKey = `${pageIndex}:${norm.x.toFixed(3)}:${norm.y.toFixed(3)}:${norm.w.toFixed(3)}:${norm.h.toFixed(3)}`
          if (seenRegions.has(regionKey)) return
          seenRegions.add(regionKey)
          const shape = boxToRectangleShape(
            documentId,
            layerId,
            pageIndex,
            `${layerId}-${finding.id ?? 'f'}-${index}`,
            norm.x,
            norm.y,
            norm.w,
            norm.h,
            finding.name ?? layerName,
            findingStyle,
          )
          if (shape) existing.push(shape)
        })
      }

      seenRegionsByLayer.set(layerName, seenRegions)
      if (existing.length > 0) shapesByLayer.set(layerName, existing)
    })
  })

  return Array.from(shapesByLayer.entries()).map(([layerName, shapes]) => ({
    id: `${documentId}-qc-layer-${slugifyLayerName(layerName)}`,
    documentId,
    name: layerName,
    color: '#F59E0B',
    visible: true,
    shapes,
  }))
}

export function hasNonZeroCoordinates(shape: Shape): boolean {
  if ('coords' in shape && shape.coords) {
    const { x, y, width, height } = shape.coords
    return !(x === 0 && y === 0 && width === 0 && height === 0)
  }
  if ('start' in shape && 'end' in shape && shape.start && shape.end) {
    const { start, end } = shape
    return !(start.x === 0 && start.y === 0 && end.x === 0 && end.y === 0)
  }
  if ('points' in shape && Array.isArray(shape.points) && shape.points.length) {
    const nonZero = shape.points.some((p) => p.x !== 0 || p.y !== 0)
    return nonZero
  }
  if ('position' in shape && shape.position) {
    return !(shape.position.x === 0 && shape.position.y === 0)
  }
  return true
}

export function filterLayersWithZeroCoordinates(layers: Layer[]): Layer[] {
  return layers
    .map((layer) => ({
      ...layer,
      shapes: layer.shapes.filter(hasNonZeroCoordinates),
    }))
    .filter((layer) => layer.shapes.length > 0)
}
